/// <amd-module name="Repos/ChangesCommon/ChangesFilesService" />
import { ItemModel } from "Repos/Common/Contracts/Git";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { DiffViewerType, IChangeItemContent, IChangesFilesService, IFileDiff, IFileDiffParameters } from "./Contracts";

interface IFileDiffQueueEntry {
    observable: ObservableValue<IFileDiff | undefined>;
    process: () => void;
    promise: Promise<IFileDiff | undefined>;
}

const fileDiffDataProvider = "ms.vss-code-web.file-diff-data-provider";
const maxConcurrentDiffRequests = 2;

export abstract class ChangesFilesService<TVersionDescriptor> extends VssService implements IChangesFilesService<TVersionDescriptor> {
    private diffOrientation: { [viewerType: number]: ObservableValue<DiffViewerOrientation> };
    private fileDiffMap: { [path: string]: IFileDiffQueueEntry } = {};

    private fileDiffQueue: IFileDiffQueueEntry[] = [];
    private outstandingDiffRequests: IFileDiffQueueEntry[] = [];

    protected repositoryId = "";

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const userPreferences = vcViewModelDataProviderService.getUserPreferences();

        this.diffOrientation = {};
        this.diffOrientation[DiffViewerType.detail] = new ObservableValue(userPreferences.diffViewerOrientation);
        this.diffOrientation[DiffViewerType.summary] = new ObservableValue(userPreferences.summaryDiffOrientation);
    }

    public getDiffOrientation(viewerType: DiffViewerType): IReadonlyObservableValue<DiffViewerOrientation> {
        return this.diffOrientation[viewerType];
    }

    public getFileDiffPromise(fileDiffParameters: IFileDiffParameters): Promise<IFileDiff | undefined> {
        return this.getFileDiffEntry(fileDiffParameters).promise;
    }

    public abstract convertDescriptorToVersion(versionDescriptor: TVersionDescriptor): string;
    public abstract convertVersionToDescriptor(version: string): TVersionDescriptor;
    public abstract getFileDownloadUrl(path: string, version: TVersionDescriptor, forceDownload?: boolean): string;
    public abstract getItemContent(path: string, version: TVersionDescriptor): IReadonlyObservableValue<IChangeItemContent | undefined>;
    public abstract getObjectId(itemModel: ItemModel): string;

    public setDiffOrientation(viewerType: DiffViewerType, orientation: DiffViewerOrientation): void {
        const currentValue = this.diffOrientation[viewerType];
        if (currentValue.value !== orientation) {
            currentValue.value = orientation;
            this.setUserNavigationPreference(viewerType, orientation);
        }
    }

    protected getFileDiffEntry(diffParameters: IFileDiffParameters): IFileDiffQueueEntry {
        if (diffParameters.originalPath && diffParameters.originalVersion?.startsWith("PS")) {
            // Edge case: file-diff-data-provider requires for Shelvesets to use same path for original and modified paths.
            // Server will resolve the original path. Otherwise it will fail in TfsVersionControlProvider.GetShelvedChanges().
            diffParameters = {
                ...diffParameters,
                originalPath: diffParameters.modifiedPath
            };
        }

        const diffKey = JSON.stringify(diffParameters);

        let fileDiffEntry = this.fileDiffMap[diffKey];
        if (!fileDiffEntry) {
            let resolvePromise: (diff: IFileDiff | undefined) => void;
            let rejectPromise: (reason: any) => void;
            const observable = new ObservableValue<IFileDiff | undefined>(undefined);
            const promise = new Promise<IFileDiff | undefined>((resolve, reject) => {
                resolvePromise = resolve;
                rejectPromise = reject;
            });

            fileDiffEntry = {
                observable,
                promise
            } as IFileDiffQueueEntry;

            fileDiffEntry.process = () => {
                const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
                const getDiffPromise = contributionService.getDataAsync<IFileDiff>(
                    fileDiffDataProvider,
                    { repositoryId: this.repositoryId, diffParameters },
                    true
                );

                getDiffPromise.then(
                    fileDiff => {
                        resolvePromise(fileDiff);
                        observable.value = fileDiff;
                        this.removeDiffQueueItem(fileDiffEntry);
                    },
                    reason => {
                        rejectPromise(reason);
                        this.removeDiffQueueItem(fileDiffEntry);
                    }
                );
            };

            this.fileDiffMap[diffKey] = fileDiffEntry;
            this.fileDiffQueue.push(fileDiffEntry);
            this.processFileDiffQueue();
        }

        return fileDiffEntry;
    }

    private processFileDiffQueue(): void {
        let queueEntry: IFileDiffQueueEntry;
        while (this.outstandingDiffRequests.length <= maxConcurrentDiffRequests && this.fileDiffQueue.length > 0) {
            queueEntry = this.fileDiffQueue.splice(0, 1)[0];
            this.outstandingDiffRequests.push(queueEntry);
            queueEntry.process();
        }
    }

    private removeDiffQueueItem(queueEntry: IFileDiffQueueEntry): void {
        this.outstandingDiffRequests = this.outstandingDiffRequests.filter(r => r !== queueEntry);
        this.processFileDiffQueue();
    }
    private setUserNavigationPreference(viewerType: DiffViewerType, diffViewerOrientation: DiffViewerOrientation) {
        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");

        const userPreferences = vcViewModelDataProviderService.getUserPreferences();
        if (viewerType === DiffViewerType.summary) {
            userPreferences.summaryDiffOrientation = diffViewerOrientation;
        } else {
            userPreferences.diffViewerOrientation = diffViewerOrientation;
        }

        vcViewModelDataProviderService.setUserPreferences(userPreferences);
    }
}
